"use client";

import type { CaptureResult } from "posthog-js";
import { useEffect } from "react";

/**
 * Boots PostHog after hydration. No-op (no init, no SDK download) when
 * NEXT_PUBLIC_POSTHOG_KEY is unset, so analytics is opt-in via env and never
 * blocks local dev or builds. The SDK is imported dynamically: posthog-js is
 * ~90KB gzipped and was flagged as unused JavaScript when bundled statically.
 * (No component reads usePostHog, so the react context provider is omitted.)
 */
let initialized = false;

// PostHog already drops exceptions with chrome-extension:// stack frames, but an
// extension's failed messaging call ("Invalid call to runtime.sendMessage(). Tab not
// found.") surfaces as a stackless unhandled rejection on our page, so it slips past.
const EXTENSION_MESSAGING_ERROR =
  /runtime\.sendMessage|Extension context invalidated|message port closed|Receiving end does not exist/;

function dropExtensionMessagingErrors(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== "$exception") return event;
  const exceptions = (event.properties?.$exception_list ?? []) as { value?: string }[];
  return exceptions.some((e) => EXTENSION_MESSAGING_ERROR.test(e.value ?? "")) ? null : event;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!key || initialized) return;
    initialized = true;
    void import("posthog-js").then(({ default: posthog }) => {
      posthog.init(key, {
        // Routed through our own /ingest reverse proxy (proxy.ts) rather than
        // us.i.posthog.com directly — same origin dodges ad-blockers and lets us
        // set our own cache lifetime on the static/array assets.
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "/ingest",
        ui_host: "https://us.posthog.com",
        // The dated defaults bundle turns on history_change pageviews — without it an
        // App Router SPA only captures the initial load, not client-side navigations.
        // "2026-01-30" is the newest date the pinned posthog-js's ConfigDefaults
        // accepts (PostHog's onboarding snippet suggests dates newer than the SDK).
        defaults: "2026-01-30",
        capture_pageleave: true,
        // PostHog Error Tracking stands in for Sentry (operator call 2026-06-10).
        // Client-side only: server errors (API routes, workers) are not captured.
        capture_exceptions: true,
        before_send: dropExtensionMessagingErrors,
        // No surveys are run; skips the ~39KB surveys.js PageSpeed flags as unused JS.
        disable_surveys: true,
        // Replays aren't used, and the WAF's XSS body rule blocks their uploads anyway.
        disable_session_recording: true,
      });
    });
  }, []);

  return <>{children}</>;
}
